// Utilidades para mostrar el resultado de la carga de productos por CSV
// (éxito y errores de validación) en modales que renderizan HTML.
// Centralizado para no duplicar el escape/markup en cada pantalla que
// dispara la carga.

export const escapeHtml = (value) =>
   String(value ?? '').replace(
      /[&<>"']/g,
      (char) =>
         ({
            '&': '&amp;',
            '<': '&lt;',
            '>': '&gt;',
            '"': '&quot;',
            "'": '&#39;',
         })[char],
   );

// Comprime una lista de números de fila en rangos legibles: [1,2,3,5] -> "1–3, 5"
// Corta a maxRangos rangos para no generar una línea kilométrica cuando el
// error se repite en cientos de filas.
const formatFilas = (filas, maxRangos = 12) => {
   const ordenadas = [...new Set(filas)].sort((a, b) => a - b);
   const rangos = [];
   let inicio = ordenadas[0];
   let anterior = ordenadas[0];
   for (let i = 1; i <= ordenadas.length; i++) {
      const actual = ordenadas[i];
      if (actual === anterior + 1) {
         anterior = actual;
         continue;
      }
      rangos.push(inicio === anterior ? `${inicio}` : `${inicio}–${anterior}`);
      inicio = actual;
      anterior = actual;
   }
   if (rangos.length > maxRangos) {
      return `${rangos.slice(0, maxRangos).join(', ')}, … (+${
         rangos.length - maxRangos
      } rango(s) más)`;
   }
   return rangos.join(', ');
};

// Arma el detalle de errores por columna a partir de la respuesta 400 de
// uploadProducts: [{ fila, columna, valor, motivo }, ...]. Agrupa por
// columna+motivo (con archivos grandes el mismo error se repite en decenas
// o cientos de filas) y muestra rangos de filas + valores de ejemplo, en un
// contenedor con scroll para que el modal no crezca sin límite.
export const buildErroresListHtml = (errores) => {
   const grupos = new Map();
   errores.forEach((e) => {
      const key = `${e.columna}::${e.motivo}`;
      if (!grupos.has(key)) {
         grupos.set(key, {
            columna: e.columna,
            motivo: e.motivo,
            filas: [],
            valores: new Set(),
         });
      }
      const grupo = grupos.get(key);
      grupo.filas.push(e.fila);
      grupo.valores.add(e.valor);
   });

   const gruposHtml = [...grupos.values()]
      .map((grupo) => {
         const valoresMuestra = [...grupo.valores]
            .slice(0, 3)
            .map((v) => `"${escapeHtml(v)}"`)
            .join(', ');
         const valoresExtra =
            grupo.valores.size > 3
               ? ` (+${grupo.valores.size - 3} valor(es) más)`
               : '';
         return `
            <div style="padding:0.6rem 0; border-bottom:1px solid #e5e7eb;">
               <div>
                  <b>${escapeHtml(grupo.columna)}</b> — ${escapeHtml(grupo.motivo)}
                  <span style="color:#9ca3af;">(${grupo.filas.length} fila${
                     grupo.filas.length === 1 ? '' : 's'
                  })</span>
               </div>
               <div style="font-size:0.82rem; color:#6b7280; margin-top:0.25rem;">
                  Filas: ${formatFilas(grupo.filas)}
               </div>
               <div style="font-size:0.82rem; color:#6b7280; margin-top:0.15rem;">
                  Ejemplo${grupo.valores.size === 1 ? '' : 's'}: ${valoresMuestra}${valoresExtra}
               </div>
            </div>
         `;
      })
      .join('');

   return `
      <p style="margin:0 0 0.6rem; color:#6b7280; font-size:0.85rem; text-align:left;">
         Corregí estas columnas en el archivo y volvé a intentar la carga.
      </p>
      <div style="text-align:left; max-height:320px; overflow-y:auto; padding-right:0.5rem;">
         ${gruposHtml}
      </div>
   `;
};

// Arma el resumen visual (creados/actualizados) del éxito de uploadProducts.
// creados/actualizados son contadores numéricos que arma el propio backend,
// no contenido del CSV: no requieren escape.
export const buildResumenCargaHtml = ({ creados = 0, actualizados = 0 }) => {
   const total = creados + actualizados;
   return `
      <p style="margin:0 0 1rem; color:#6b7280; font-size:0.95rem;">
         Se procesaron ${total} producto${total === 1 ? '' : 's'} en total.
      </p>
      <div style="display:flex; justify-content:center; gap:2.5rem;">
         <div style="text-align:center;">
            <div style="font-size:2rem; font-weight:700; line-height:1; color:#3B82F6;">${creados}</div>
            <div style="font-size:0.85rem; color:#6b7280; margin-top:0.35rem;">creado${creados === 1 ? '' : 's'}</div>
         </div>
         <div style="text-align:center;">
            <div style="font-size:2rem; font-weight:700; line-height:1; color:#22c55e;">${actualizados}</div>
            <div style="font-size:0.85rem; color:#6b7280; margin-top:0.35rem;">actualizado${actualizados === 1 ? '' : 's'}</div>
         </div>
      </div>
   `;
};
